/**
 * HiddenActions — usynlige DOM-knapper som speiler de viktigste Phaser-
 * aksjonene. Skjermlesere og tastatur-brukere finner dem via Tab, og
 * `onclick` trigger nøyaktig samme handler som tilsvarende Phaser-knapp.
 *
 * Bygd i runde 42. Designvalg:
 * - Visuelt skjult med standard `clip`-teknikk (samme som AriaMirror)
 *   slik at synet ikke forstyrres, men ikke `display:none` så
 *   skjermleser fortsatt ser dem.
 * - `tabindex="0"` slik at Tab-navigasjon finner dem etter HUD.
 * - Tilstand: knapper kan disables når aksjonen ikke er tilgjengelig
 *   (f.eks. Innboks er kun i Redaksjon-etasjen).
 *
 * Bruk:
 *   const ha = new HiddenActions();
 *   ha.mount();
 *   ha.settHandler('innboks', () => scene.openInnboks());
 *   ha.settTilgjengelig('innboks', true);
 */

export type HiddenActionId =
  | 'neste-dag'
  | 'innboks'
  | 'forfattere'
  | 'direksjon'
  | 'min-karriere'
  | 'mute';

const ROT_ID = 'sp-hidden-actions';

const SKJULT_STIL = [
  'position:absolute',
  'left:-9999px',
  'width:1px',
  'height:1px',
  'overflow:hidden',
  'clip:rect(0 0 0 0)',
  'clip-path:inset(50%)',
  'white-space:nowrap',
].join(';');

interface AksjonsKonfig {
  id: HiddenActionId;
  label: string;
}

const AKSJONER: ReadonlyArray<AksjonsKonfig> = [
  { id: 'neste-dag', label: 'Neste dag' },
  { id: 'innboks', label: 'Åpne innboks' },
  { id: 'forfattere', label: 'Åpne forfattere' },
  { id: 'direksjon', label: 'Gå til direksjonen' },
  { id: 'min-karriere', label: 'Åpne min karriere' },
  { id: 'mute', label: 'Skru av eller på lyd' },
];

export class HiddenActions {
  private rot?: HTMLDivElement;
  private knapper: Map<HiddenActionId, HTMLButtonElement> = new Map();
  private handlere: Map<HiddenActionId, () => void> = new Map();
  private tilgjengelig: Map<HiddenActionId, boolean> = new Map();

  mount(): void {
    if (typeof document === 'undefined') return;

    // Rens evt forrige
    const eksisterende = document.getElementById(ROT_ID);
    if (eksisterende?.parentNode) {
      eksisterende.parentNode.removeChild(eksisterende);
    }

    const rot = document.createElement('div');
    rot.id = ROT_ID;
    rot.setAttribute('role', 'group');
    rot.setAttribute('aria-label', 'Hurtigvalg for skjermleser');
    rot.setAttribute('style', SKJULT_STIL);
    document.body.appendChild(rot);
    this.rot = rot;

    this.knapper.clear();
    for (const aksjon of AKSJONER) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.id = `${ROT_ID}-${aksjon.id}`;
      btn.setAttribute('aria-label', aksjon.label);
      btn.dataset.aksjon = aksjon.id;
      btn.tabIndex = 0;
      btn.textContent = aksjon.label;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handler = this.handlere.get(aksjon.id);
        if (handler && this.tilgjengelig.get(aksjon.id) !== false) {
          handler();
        }
      });
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });
      rot.appendChild(btn);
      this.knapper.set(aksjon.id, btn);
      this.tilgjengelig.set(aksjon.id, true);
    }
  }

  destroy(): void {
    if (this.rot?.parentNode) this.rot.parentNode.removeChild(this.rot);
    this.rot = undefined;
    this.knapper.clear();
    this.handlere.clear();
    this.tilgjengelig.clear();
  }

  /**
   * Setter handler for en aksjon. Når brukeren trigger DOM-knappen
   * (via klikk, Tab+Enter eller skjermleser) kalles denne.
   */
  settHandler(id: HiddenActionId, handler: () => void): void {
    this.handlere.set(id, handler);
  }

  /**
   * Marker en aksjon som tilgjengelig (true) eller ikke (false).
   * Når false: `aria-disabled` settes og handler kalles ikke.
   */
  settTilgjengelig(id: HiddenActionId, tilgjengelig: boolean): void {
    this.tilgjengelig.set(id, tilgjengelig);
    const btn = this.knapper.get(id);
    if (btn) {
      if (tilgjengelig) {
        btn.removeAttribute('aria-disabled');
      } else {
        btn.setAttribute('aria-disabled', 'true');
      }
    }
  }

  /**
   * Slår av tab-fokus på samtlige knapper midlertidig. Brukes når en
   * modal er åpen (fokus skal være inni modalen). `gjenopprett` setter
   * tabindex tilbake til 0.
   */
  settTabbable(tabbable: boolean): void {
    for (const btn of this.knapper.values()) {
      btn.tabIndex = tabbable ? 0 : -1;
    }
  }

  /** Returnerer knappen for en aksjon — brukes av tester. */
  hentKnapp(id: HiddenActionId): HTMLButtonElement | undefined {
    return this.knapper.get(id);
  }

  /** Liste over alle knapp-IDer — brukes av tester og ModalFokusTrap. */
  alleIder(): HiddenActionId[] {
    return AKSJONER.map((a) => a.id);
  }
}

export const hiddenActions = new HiddenActions();
